import { ChevronsUpDown, X } from "lucide-react";
import { useTranslate } from "ra-core";

import { Button } from "@/components/ui/button";

import { CreatableCombobox } from "./CreatableCombobox";
import { useSourcesByUsage } from "./useSourcesByUsage";

export interface SourcePickerProps {
  value: string | null;
  onChange: (source: string | null) => void;
}

export const SourcePicker = ({ value, onChange }: SourcePickerProps) => {
  const translate = useTranslate();
  const sources = useSourcesByUsage();
  const options = sources.map((source) => ({
    value: source.name,
    label: source.name,
    count: source.nb_contacts,
  }));
  // A source typed in this session is not in the list until it is saved
  const allOptions =
    value && !options.some((option) => option.value === value)
      ? [{ value, label: value }, ...options]
      : options;

  return (
    <div className="flex items-center gap-1">
      <CreatableCombobox
        options={allOptions}
        selected={value ? [value] : []}
        onSelect={(source) => onChange(source === value ? null : source)}
        onCreate={(source) => onChange(source)}
        placeholder={translate("crm.operations.placeholders.search_source")}
        closeOnSelect
      >
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 justify-between gap-2 font-normal"
          aria-label={translate("crm.operations.fields.source")}
        >
          <span className={value ? "" : "text-muted-foreground"}>
            {value ?? translate("crm.operations.combobox.none")}
          </span>
          <ChevronsUpDown className="opacity-50" />
        </Button>
      </CreatableCombobox>
      {value && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-8"
          onClick={() => onChange(null)}
          aria-label={translate("crm.operations.combobox.remove", {
            name: value,
          })}
        >
          <X />
        </Button>
      )}
    </div>
  );
};
